import React, { useCallback, useMemo } from 'react';
import { CatalogContext, getIsCashfundType, getIsExperienceType } from '.';
import type {
  CatalogProviderProps,
  CatalogContextProps,
  CatalogDepartmentTreeRoot,
  CatalogDepartment,
  CatalogDepartmentCategory,
  CatalogDepartmentCategorySubcategory,
} from './Catalog.types';

import { useSharedCatalogMutation } from '../../Catalog.controller';
import { useRetryAddCatalogItemAttempt } from '../../useRetryAddCatalogItemAttempt';
import { useCatalogRouting } from '../../useCatalogRouting';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { CatalogTelemetryProvider, useCatalogRegistryTelemetry } from '../../Catalog.telemetry';
import { RefinementsPanelProvider } from '../../components/RefinementsPanel';
import { MobileProductQuickAddDrawerProvider } from '../../components/ProductTile/components/MobileProductQuickAddDrawer/MobileProductQuickAddDrawer.provider';
import { CatalogSearchBarProvider } from '../../components/CatalogSearchBar';
import type { UseCatalogStateReturn } from '../../useCatalogState';
import { useCatalogState } from '../../useCatalogState';
import { useAutoAddCatalogItemOnMount } from '../../hooks/useAutoAddCatalogItemOnMount';
import { CashFundDialogProvider, useCashFundDialogContext } from '../../components/CashFundDialogProvider';
import { ValuePropCardProvider } from '../../components/ProductTile/components/ValuePropCard/ValuePropCard.provider';
import { useResetLocationStateUponPageReload } from '../../hooks/useTrackCatalogPageViaLocationState';
import { CatalogLoggingProvider } from '../../Catalog.logging';
import { RecentlyAddedRegistryItemUpdateDrawer } from '../../components/RecentlyAddedRegistryItemUpdateDrawer';
import { useRegistryItemProductStatus } from '@apps/registry/admin/hooks/useRegistryItemProductStatus';
import { EditGiftDialog } from '@apps/registry/admin/components/EditGift';

const CatalogProviderRoot: React.FC<CatalogProviderProps & Omit<UseCatalogStateReturn, 'searchQuery' | 'setSearchQuery'>> = ({
  children,
  addToRegistryAttemptInfo,
  donationFundInfo,
  registryShippingAddress,
  removeAddAttemptInfo,
  eventId,
  eventType,
  eventHandle,
  onAddItemToRegistryWithoutEvent,
  registryCurrency,
  hideMobileSideBarNav,
  isSidebarNavMobileOpen,
  selectedCategory,
  toggleSidebarNavMobile,
  redirectPath,
}) => {
  const { openAddCatalogCashFundDialog, openCustomCashFundDialog } = useCashFundDialogContext();
  const isCashFundType = useMemo(() => {
    return getIsCashfundType(selectedCategory?.publicUrlPath, selectedCategory?.lineageCsp);
  }, [selectedCategory]);

  const isExperienceType = useMemo(() => {
    return getIsExperienceType(selectedCategory?.publicUrlPath, selectedCategory?.lineageCsp);
  }, [selectedCategory]);

  useCatalogRegistryTelemetry({ addToContext: { eventId } });
  const { goToCategory, goToCatalogItem, goToCurationDetails, goToRootCatalog, goToRootCurations, isAdminView } = useCatalogRouting();

  const isMobile = useIsMobileScreen();

  const onAddCatalogItemSuccess = useCallback(() => {
    removeAddAttemptInfo?.();
  }, [removeAddAttemptInfo]);

  const { addCatalogItemToRegistry, addCatalogItemLoading, addCatalogItemError, notificationVisible, setNotificationVisible } = useSharedCatalogMutation({
    eventId,
    eventHandle,
    onAddItemToRegistryWithoutEvent,
    onAddCatalogItemSuccess,
    selectedCategory,
  });

  useRetryAddCatalogItemAttempt({ addCatalogItemToRegistry, addToRegistryAttemptInfo, eventId, openAddCatalogCashFundDialog, openCustomCashFundDialog, removeAddAttemptInfo });
  useAutoAddCatalogItemOnMount({
    eventId,
    addCatalogItem: addCatalogItemToRegistry,
    openAddCatalogCashFundDialog,
    openCustomCashFundDialog,
  });

  useResetLocationStateUponPageReload();

  const categoryHasChildren = useCallback(
    (
      category: Maybe<CatalogDepartment | CatalogDepartmentTreeRoot | CatalogDepartmentCategory | CatalogDepartmentCategorySubcategory>,
    ): category is CatalogDepartment | CatalogDepartmentTreeRoot | CatalogDepartmentCategory => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return !!(category as any)?.children;
    },
    [],
  );
  const { isProductInRegistry, onOpenEditGiftDialog, currentCookedProduct, isOpenEditGiftDialog, onCloseEditGiftDialog } = useRegistryItemProductStatus({ eventHandle });

  const ctx = useMemo<CatalogContextProps>(() => {
    return {
      donationFundInfo,
      isMobile: !!isMobile,
      // State
      addCatalogItemLoading,
      addCatalogItemError,
      eventId,
      eventType,
      eventHandle,
      isAdminView,
      isSidebarNavMobileOpen,
      notificationVisible,
      registryCurrency,
      registryShippingAddress,
      selectedCategory,
      isCashFundType,
      isExperienceType,
      redirectPath,

      // Operations
      addCatalogItemToRegistry,
      categoryHasChildren,
      goToCategory,
      goToCatalogItem,
      goToRootCatalog,
      goToRootCurations,
      goToCurationDetails,
      hideMobileSideBarNav,
      onAddItemToRegistryWithoutEvent,
      setNotificationVisible,
      toggleSidebarNavMobile,
      isProductInRegistry,
      onOpenEditGiftDialog,
      isOpenEditGiftDialog,
    };
  }, [
    categoryHasChildren,
    donationFundInfo,
    isMobile,
    eventId,
    eventType,
    eventHandle,
    hideMobileSideBarNav,
    isAdminView,
    isSidebarNavMobileOpen,
    notificationVisible,
    registryShippingAddress,
    selectedCategory,
    isCashFundType,
    isExperienceType,
    toggleSidebarNavMobile,
    addCatalogItemToRegistry,
    addCatalogItemLoading,
    addCatalogItemError,
    goToCategory,
    goToCatalogItem,
    goToRootCatalog,
    goToRootCurations,
    goToCurationDetails,
    onAddItemToRegistryWithoutEvent,
    setNotificationVisible,
    registryCurrency,
    redirectPath,
    isProductInRegistry,
    onOpenEditGiftDialog,
    isOpenEditGiftDialog,
  ]);

  return (
    <CatalogContext.Provider value={ctx}>
      <>{children}</>
      {eventId && currentCookedProduct && (
        <EditGiftDialog
          eventId={eventId}
          isOpen={isOpenEditGiftDialog}
          product={currentCookedProduct}
          onClose={onCloseEditGiftDialog}
          currency={'$'}
          showCurrencyDialog={() => {}}
        />
      )}
    </CatalogContext.Provider>
  );
};

export const CatalogProvider: React.FC<CatalogProviderProps> = props => {
  const { children, ...restProps } = props;
  const { searchQuery, setSearchQuery, ...catalogState } = useCatalogState();
  return (
    <CatalogTelemetryProvider>
      <CatalogLoggingProvider selectedCategory={catalogState.selectedCategory}>
        <CashFundDialogProvider
          selectedCategory={catalogState.selectedCategory}
          eventId={props.eventId}
          donationFundInfo={props.donationFundInfo}
          registryCurrency={props.registryCurrency}
        >
          <CatalogProviderRoot
            {...restProps}
            {...catalogState}
          >
            <CatalogSearchBarProvider
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            >
              <RefinementsPanelProvider>
                <MobileProductQuickAddDrawerProvider>
                  <ValuePropCardProvider>{children}</ValuePropCardProvider>

                  <RecentlyAddedRegistryItemUpdateDrawer />
                </MobileProductQuickAddDrawerProvider>
              </RefinementsPanelProvider>
            </CatalogSearchBarProvider>
          </CatalogProviderRoot>
        </CashFundDialogProvider>
      </CatalogLoggingProvider>
    </CatalogTelemetryProvider>
  );
};
